import { Component, Input, OnChanges } from '@angular/core';
import { ChangesHistoryHelperService } from '@client/app/pages/global/changes-history/services/changes-history-helper/changes-history-helper.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'changes-history-dashboard-table-data-privacy',
    standalone: false,
    template: `
        <div class="row">
            <div class="col-48">
                <table class="table table-hover table-fixed">
                    <thead>
                    <tr>
                        <th class="col-8 col-lg-7">
                            {{'Crawled at' | translate}}
                        </th>
                        <th class="text-right col-4 col-lg-8">
                            <span class="visible-print-block">{{'Pages' | translate}}</span>
                            <span class="hidden-print">{{'Pages crawled' | translate}}</span>
                        </th>
                        <th class="text-right col-8 col-lg-9">
                            {{'Domain compliance' | translate}}
                        </th>
                        <th class="text-right col-22 col-lg-16">
                            <span class="visible-print-block">{{'Issues by Severities' | translate}}</span>
                            <span class="hidden-print">{{'Issues by Severities' | translate}}</span>
                        </th>
                        <th class="text-right col-6 col-lg-7">
                            <span class="visible-print-block">{{'Changes' | translate}}</span>
                            <span class="hidden-print">{{'Changes since previous crawl' | translate}}</span>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr *ngFor="let story of sortedHistory">
                        <td class="text-v-middle">
                            <user-date [twoLines]="true"
                                       [date]="story.post_processing_done_at || story.date">
                            </user-date>
                        </td>
                        <td class="text-v-middle text-right">
                            {{getFormattedPageCount(story.page_count)}}
                        </td>
                        <td class="text-right text-v-middle">
                            <table-css-compliance
                                    class="donut"
                                    *ngIf="story.data_protection_pages_with_violations_count !== null"
                                    [totalCount]="story.page_count"
                                    [errorCount]="story.data_protection_pages_with_violations_count">
                            </table-css-compliance>
                            <span *ngIf="story.data_protection_pages_with_violations_count === null" translate>
                                N/A
                            </span>
                        </td>
                        <td class="text-v-middle text-right">
                            <ul class="list-inline pl-4 mt-3">
                                <li [ngbTooltip]="highSeverityTooltip" triggers="focus hover">
                                    <span class="fas fa-circle high" aria-hidden="true"></span>
                                    <span class="sr-only" translate>High severity issues</span>
                                    <span class="ml-1">{{ story.data_protection_high_priority_page_violations_count || 0 }}</span>
                                </li>
                                <li [ngbTooltip]="mediumSeverityTooltip" triggers="focus hover">
                                    <span class="fas fa-circle medium" aria-hidden="true"></span>
                                    <span class="sr-only" translate>Medium severity issues</span>
                                    <span class="ml-1">{{ story.data_protection_medium_priority_page_violations_count || 0 }}</span>
                                </li>
                                <li [ngbTooltip]="lowSeverityTooltip" triggers="focus hover">
                                    <span class="fas fa-circle low" aria-hidden="true"></span>
                                    <span class="sr-only" translate>Low severity issues</span>
                                    <span class="ml-1">{{ story.data_protection_low_priority_page_violations_count || 0 }}</span>
                                </li>
                            </ul>

                            <ng-template #highSeverityTooltip>
                                {{'High severity issues' | translate}}
                            </ng-template>
                            <ng-template #mediumSeverityTooltip>
                                {{'Medium severity issues' | translate}}
                            </ng-template>
                            <ng-template #lowSeverityTooltip>
                                {{'Low severity issues' | translate}}
                            </ng-template>
                        </td>
                        <td class="text-v-middle text-right"
                            [ngClass]="getChangeValueColorClass(story.id)">
                            <span class="fas" [ngClass]="getChangeValueIconClass(story.id)">
                            </span>
                            {{getPercentageChange(story.id)}}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `,
})
export class DataPrivacyComponent implements OnChanges {
    @Input() history: any;
    @Input() domain: any;
    @Input() domainGroup: any;

    sortedHistory: any[] = [];

    issuesStack: any = [
        'data_protection_high_priority_page_violations_count',
        'data_protection_medium_priority_page_violations_count',
        'data_protection_low_priority_page_violations_count',
    ];

    constructor (
        private changesHistoryHelperService: ChangesHistoryHelperService,
        private pipeService: PipeService,
    ) {}

    ngOnChanges (): void {
        if (!this.history) {
            return;
        }
        this.sortedHistory = safeClone(this.history).sort((a, b) => {
            return b.id - a.id;
        });
    }

    getChangeValue (id: any): any {
        return this.changesHistoryHelperService.getChangeValue(id, this.history, this.issuesStack);
    }

    getChangePercentageValue (id: any): any {
        return this.changesHistoryHelperService.getChangePercentageValue(id, this.history, this.issuesStack);
    }

    getFormattedPageCount (count: any): any {
        return this.pipeService.get('monNumber')(count);
    }

    getPercentageChange (id: any): any {
        const value = this.getChangePercentageValue(id) / 100;
        return this.pipeService.get('monNumber')(value, '0.[00]%');
    }

    getChangeValueColorClass (id: any): any {
        const changeFromLast = this.getChangeValue(id);
        return {
            'text-success': changeFromLast < 0,
            'text-danger': changeFromLast > 0,
            'text-grey': changeFromLast === 0,
        };
    }

    getChangeValueIconClass (id: any): any {
        const changeFromLast = this.getChangeValue(id);
        return {
            'fa-chevron-down': changeFromLast < 0,
            'fa-chevron-up': changeFromLast > 0,
        };
    }
}
